// ─── 타입 ────────────────────────────────────────────────────────────────────

interface MenuCategory {
    slug: string;
    name: string;
}

interface MenuItemData {
    id: number;
    slug: string;
    category: string;
    name_ko: string;
    name_en: string;
    before_price: string;
    price: string;
    thumb_url: string;
    tags: string[];
}

interface ApiResponse {
    success: boolean;
    data: {
        categories: MenuCategory[];
        items: MenuItemData[];
    };
}

interface MenuAssets {
    showRoute: string;
    particleUrl: string;
    fallbackUrl: string;
}

// ─── MenuPlaceholder ─────────────────────────────────────────────────────────

class MenuPlaceholder {
    private el: HTMLElement;

    constructor(el: HTMLElement) {
        this.el = el;
    }

    show(): void {
        this.el.hidden = false;
    }

    hide(): void {
        this.el.hidden = true;
    }
}

// ─── MenuItem ────────────────────────────────────────────────────────────────

class MenuItem {
    static create(item: MenuItemData, index: number, assets: MenuAssets): HTMLElement {
        const article = document.createElement('article');
        article.className = 'item' + (index === 2 ? ' is-featured' : '');
        article.setAttribute('data-menu-item', '');
        article.setAttribute('data-category', item.category);

        const href = assets.showRoute.replace('__SLUG__', item.slug);
        const imgSrc = item.thumb_url || assets.fallbackUrl;
        const tagsHtml = item.tags
            .map((t, ti) => (ti > 0 ? ' ' : '') + '#' + MenuItem.escHtml(t))
            .join('');
        const priceHtml = (item.before_price && item.before_price !== item.price)
            ? `<span class="no-body-lg --700 wh7">${MenuItem.escHtml(item.before_price)}</span>`
            : '';

        article.innerHTML = `
<a href="${MenuItem.escAttr(href)}" class="link" aria-label="${MenuItem.escAttr(item.name_ko)}">
  <figure class="visual no-mg-md--b">
    <img class="particle" src="${MenuItem.escAttr(assets.particleUrl)}" alt="" aria-hidden="true">
    <img class="food" src="${MenuItem.escAttr(imgSrc)}" alt="${MenuItem.escAttr(item.name_ko)}" loading="lazy">
    <h3 class="visual-title no-en no-heading-lg">${MenuItem.escHtml(item.name_en)}</h3>
  </figure>
  <div class="txt --tac">
    <p class="no-body-lg --700">${MenuItem.escHtml(item.name_ko)}</p>
    <p class="no-body-md wh7">${tagsHtml}</p>
    <div class="price no-mg-sm--t">
      ${priceHtml}
      <strong class="no-heading-sm --700">${MenuItem.escHtml(item.price)}</strong>
    </div>
  </div>
</a>`;
        return article;
    }

    private static escHtml(str: string): string {
        return str
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    private static escAttr(str: string): string {
        return str.replace(/"/g, '&quot;');
    }
}

// ─── MenuList ────────────────────────────────────────────────────────────────

class MenuList {
    private root: HTMLElement;
    private tabsEl: HTMLElement;
    private gridEl: HTMLElement;
    private placeholder: MenuPlaceholder | null;
    private assets: MenuAssets;
    private apiUrl: string = '';
    private activeCategory = '';
    private debounceTimer: ReturnType<typeof setTimeout> | null = null;
    private categoriesRendered = false;

    constructor(root: HTMLElement, placeholder: MenuPlaceholder | null) {
        this.root = root;
        this.tabsEl = root.querySelector<HTMLElement>('[data-menu-tabs]')!;
        this.gridEl = root.querySelector<HTMLElement>('[data-menu-grid]')!;
        this.placeholder = placeholder;
        this.assets = {
            showRoute:   root.getAttribute('data-menu-show-route') ?? '',
            particleUrl: root.getAttribute('data-menu-particle') ?? '',
            fallbackUrl: root.getAttribute('data-menu-fallback') ?? '',
        };
    }

    async load(apiUrl: string): Promise<void> {
        this.apiUrl = apiUrl;

        // 카테고리 목록을 얻기 위해 첫 요청은 전체로 날림 (items는 버림)
        const catJson = await this.fetchItems('');
        if (!catJson) return;

        const { categories } = catJson.data;
        if (!this.categoriesRendered) {
            this.renderCategories(categories);
            this.categoriesRendered = true;
        }

        // 첫 번째 카테고리로 필터된 items를 재요청
        const firstSlug = categories[0]?.slug ?? '';
        const itemJson = firstSlug ? await this.fetchItems(firstSlug) : catJson;
        if (!itemJson) return;

        this.renderItems(itemJson.data.items);
    }

    private async fetchItems(categorySlug: string): Promise<ApiResponse | null> {
        const url = categorySlug
            ? `${this.apiUrl}${this.apiUrl.includes('?') ? '&' : '?'}category=${encodeURIComponent(categorySlug)}`
            : this.apiUrl;

        let response: Response;
        try {
            response = await fetch(url, { headers: { 'Accept': 'application/json' } });
        } catch {
            return null;
        }

        if (!response.ok) return null;

        try {
            const json = await response.json() as ApiResponse;
            return json.success && json.data ? json : null;
        } catch {
            return null;
        }
    }

    private renderCategories(categories: MenuCategory[]): void {
        this.tabsEl.innerHTML = '';

        if (!categories.length) return;

        categories.forEach((cat, i) => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'tab no-body-md --700 wh7' + (i === 0 ? ' is-active bc' : '');
            btn.setAttribute('data-menu-tab', cat.slug);
            btn.textContent = cat.name;
            this.tabsEl.appendChild(btn);
        });

        this.activeCategory = categories[0]?.slug ?? '';
        this.bindTabs();
    }

    private renderItems(items: MenuItemData[]): void {
        this.gridEl.innerHTML = '';

        if (!items.length) {
            this.placeholder?.show();
            return;
        }

        this.placeholder?.hide();
        items.forEach((item, i) => {
            this.gridEl.appendChild(MenuItem.create(item, i, this.assets));
        });
    }

    private bindTabs(): void {
        const tabs = Array.from(this.tabsEl.querySelectorAll<HTMLElement>('[data-menu-tab]'));
        tabs.forEach((tab) => {
            tab.addEventListener('click', () => {
                const slug = tab.getAttribute('data-menu-tab') ?? '';
                this.onTabClick(slug);
            });
        });
    }

    private onTabClick(categorySlug: string): void {
        if (categorySlug === this.activeCategory) return;

        this.activeCategory = categorySlug;
        this.updateTabActive(categorySlug);

        if (this.debounceTimer !== null) {
            clearTimeout(this.debounceTimer);
        }

        this.debounceTimer = setTimeout(() => {
            this.debounceTimer = null;
            this.gridEl.classList.add('is-switching');

            this.fetchItems(categorySlug).then((json) => {
                requestAnimationFrame(() => {
                    this.renderItems(json?.data.items ?? []);
                    requestAnimationFrame(() => {
                        this.gridEl.classList.remove('is-switching');
                    });
                });
            });
        }, 150);
    }

    private updateTabActive(categorySlug: string): void {
        const tabs = Array.from(this.tabsEl.querySelectorAll<HTMLElement>('[data-menu-tab]'));
        tabs.forEach((tab) => {
            const active = tab.getAttribute('data-menu-tab') === categorySlug;
            tab.classList.toggle('is-active', active);
            tab.classList.toggle('bc', active);
        });
    }
}

// ─── export ──────────────────────────────────────────────────────────────────

export const MenuController = {
    index(): void {
        const section = document.querySelector<HTMLElement>('.sub-menu');
        if (!section) return;

        const apiUrl = section.getAttribute('data-menu-api') ?? '';
        if (!apiUrl) return;

        const placeholderEl = section.querySelector<HTMLElement>('[data-menu-empty]');
        const placeholder = placeholderEl ? new MenuPlaceholder(placeholderEl) : null;

        new MenuList(section, placeholder).load(apiUrl).catch(() => {});
    },
    show(): void {},
};
